import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import {
    type DeviceOverrideSetting,
    DeviceOverrideSettingString,
    getZodSchemaFromClass,
    validate,
} from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    ManualOverrideInfoFieldsWithDefaults,
    ManualOverrideInfoSchemaTypes,
} from "../types/manualOverrideInfo.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

export type { DeviceOverrideSetting } from "../../zod-decorator/index.js";

const SCHEMA_KEY = "ManualOverrideInfo" as const;

/**
 * Determines the ideal manual override state based on device and remote settings.
 * Resolves conflicts between device-initiated and remotely-initiated settings
 * by using the most recently updated one when both are defined.
 *
 * @param manualOverrideInfo - Device and remote override settings
 * @returns The resolved manual override setting to use
 */
const getIdealManualOverrideState = (manualOverrideInfo: ManualOverrideInfo): DeviceOverrideSetting => {
    // if only one is defined, fallback to the defined one or otherwise "none"
    if (!manualOverrideInfo.deviceOverrideSetting || !manualOverrideInfo.remoteOverrideSetting) {
        return manualOverrideInfo.deviceOverrideSetting ?? manualOverrideInfo.remoteOverrideSetting ?? "none";
    }

    // use the setting updated most recently
    if (
        manualOverrideInfo.deviceOverrideSettingLastUpdated.valueOf() >
        manualOverrideInfo.remoteOverrideSettingLastUpdated.valueOf()
    ) {
        return manualOverrideInfo.deviceOverrideSetting;
    }

    return manualOverrideInfo.remoteOverrideSetting;
};

// TODO: convert to responseOnly schema class
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<ManualOverrideInfo>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ManualOverrideInfo extends SchemaBase<ManualOverrideInfo, ManualOverrideInfoFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: DeviceOverrideSettingString.optional() })
    @prop({ type: () => String, required: false })
    public remoteOverrideSetting?: DeviceOverrideSetting;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public remoteOverrideSettingLastUpdated?: Date;

    @validate({ zodValidator: DeviceOverrideSettingString.optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public deviceOverrideSetting?: DeviceOverrideSetting;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public deviceOverrideSettingLastUpdated?: Date;

    static getters = {
        getIdealManualOverrideState: (self: ManualOverrideInfo) => getIdealManualOverrideState(self),
    } as const satisfies Record<string, (self: ManualOverrideInfo) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<ManualOverrideInfoSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ManualOverrideInfo, ManualOverrideInfoFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

ManualOverrideInfo.dtoValidators = getZodSchemaFromClass(
    ManualOverrideInfo,
) as unknown as DtoValidatorTypes<ManualOverrideInfoSchemaTypes>;
